'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import {
  BillingCard,
  DeliveryCard,
} from '@eliadmin/core/src/components/nt/account/profile/address-card';
import { CommunicationCard } from '@eliadmin/core/src/components/nt/account/profile/communication-card';
import { CompanyCard } from '@eliadmin/core/src/components/nt/account/profile/company-card';
import { DeleteAccount } from '@eliadmin/core/src/components/nt/account/profile/delete-account';
import { PersonalCard } from '@eliadmin/core/src/components/nt/account/profile/personal-card';
import {
  profileSchema,
  type ProfileFormInput,
  type ProfileFormValues,
} from '@eliadmin/core/src/components/nt/account/profile/profile-schema';
import {
  SERVER_FIELD_MAP,
  toCustomerUpdate,
  toProfileFormValues,
} from '@eliadmin/core/src/components/nt/account/profile/profile-values';
import { useCountries } from '@eliadmin/core/src/components/nt/account/profile/use-countries';
import {
  FormResultMessage,
  type FormResult,
} from '@eliadmin/core/src/components/nt/account/shell/form-result';
import { applyServerErrors } from '@eliadmin/core/src/components/nt/account/shell/server-errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { CheckoutTexts } from '@eliadmin/core/src/utils/nt/checkout';

/**
 * The cards of „Môj profil“ (Figma „Content“ 205:11129) as one form. „Uložiť zmeny“ sends the
 * customer fields with `updateCustomer` and puts the answer into the customer store like the core
 * profile form; a 422 goes onto the fields it names, anything else is reported next to the button.
 * The newsletter toggle acts on its own (core newsletter API). A success disappears with the next
 * edit.
 */
export function ProfileForm({
  customer,
  authToken,
  checkoutTexts,
}: {
  customer: Customer;
  authToken: string;
  checkoutTexts: CheckoutTexts;
}) {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(s => s.currentLocale);
  const layoutCountry = useGlobalStore(s => s.generalLayout.country?.id);
  const countries = useCountries();
  const defaultCountry = countries.some(country => country.id === layoutCountry)
    ? layoutCountry
    : (countries[0]?.id ?? '');
  const isCompany = !!customer.company_buy;
  const [result, setResult] = useState<FormResult>(null);

  const form = useForm<ProfileFormInput, unknown, ProfileFormValues>({
    resolver: zodResolver(
      profileSchema(
        formValidationMessages,
        isCompany,
        checkoutTexts.streetNumberMissing || undefined,
      ),
    ),
    mode: 'onTouched',
    defaultValues: toProfileFormValues(customer, defaultCountry),
  });
  const sameDelivery = useWatch({ control: form.control, name: 'same_delivery' });
  const { isDirty, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async values => {
    setResult(null);
    try {
      const { data } = await updateCustomer({
        locale,
        authToken,
        data: toCustomerUpdate(values, countries, isCompany),
      });
      const updated = { ...customer, ...data };
      initCustomerStore({ authToken, customer: updated });
      form.reset(toProfileFormValues(updated, defaultCountry));
      setResult({ status: 'success', message: t('text3119') });
    } catch (error) {
      const rest = applyServerErrors(error, form.setError, field => SERVER_FIELD_MAP[field]);
      if (rest) setResult({ status: 'failed', message: rest.join('\n') || t('text2307') });
    }
  });

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
        <PersonalCard isCompany={isCompany} checkoutTexts={checkoutTexts} />
        {isCompany ? <CompanyCard checkoutTexts={checkoutTexts} /> : null}
        <BillingCard countries={countries} isCompany={isCompany} checkoutTexts={checkoutTexts} />
        {sameDelivery ? null : <DeliveryCard countries={countries} checkoutTexts={checkoutTexts} />}
        <CommunicationCard customer={customer} authToken={authToken} />
        {/* Figma „Actions“: 8 px above, the button, the result, „Zrušiť konto“ on the right. */}
        <div className="flex flex-col gap-4 pt-2 @[36rem]:flex-row @[36rem]:items-center">
          <Button
            type="submit"
            iconLeft="check"
            isLoading={isSubmitting}
            className="w-full @[36rem]:w-auto"
          >
            {t('text2290')}
          </Button>
          <FormResultMessage
            result={result?.status === 'success' && isDirty ? null : result}
            className="empty:-mt-4 @[36rem]:flex-1 @[36rem]:empty:mt-0"
          />
          <DeleteAccount authToken={authToken} />
        </div>
      </form>
    </FormProvider>
  );
}
